/**
 * Fleet Service Record Slice
 *
 * State for the fleet portal's Service History page (CLIENT users):
 * - Paginated list for a date range, newest first
 * - Per-record detail cache (expanding a row fetches once, then re-expands free)
 *
 * Everything is scoped server-side by the customerUuid JWT claim.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fleetServiceRecordMgtApi, {
    listFleetServiceRecordsApi,
    getFleetServiceRecordApi,
} from '../../services/fleetServiceRecordMgtApi.js';

// ============================================
// Async Thunks
// ============================================

export const fetchFleetServiceRecords = createAsyncThunk(
    'fleetServiceRecords/fetchFleetServiceRecords',
    async ({ from, to, page = 0, size = 20 }, { rejectWithValue }) => {
        try {
            const data = await listFleetServiceRecordsApi({ from, to, page, size });
            return { data, from, to };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load your service history.';
            return rejectWithValue(message);
        }
    }
);

export const fetchFleetServiceRecordDetail = createAsyncThunk(
    'fleetServiceRecords/fetchFleetServiceRecordDetail',
    async (id, { rejectWithValue }) => {
        try {
            const data = await getFleetServiceRecordApi(id);
            return { id, data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load this service.';
            return rejectWithValue({ id, message });
        }
    }
);

// ============================================
// Slice
// ============================================

const emptyList = {
    records: [],
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    loading: false,
    error: null,
    // Echoed back so the page can show what window is displayed.
    appliedRange: null,
};

const initialState = {
    list: { ...emptyList },
    // Keyed by record id: { data, loading, error }
    detailById: {},
};

const fleetServiceRecordSlice = createSlice({
    name: 'fleetServiceRecords',
    initialState,
    reducers: {
        clearFleetServiceRecords: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // ---- List ----
            .addCase(fetchFleetServiceRecords.pending, (state, action) => {
                state.list = {
                    ...state.list,
                    loading: true,
                    error: null,
                    page: action.meta.arg.page ?? 0,
                };
            })
            .addCase(fetchFleetServiceRecords.fulfilled, (state, action) => {
                const { data, from, to } = action.payload;
                state.list = {
                    // Backend PageResponse: { content, totalPages, totalElements, number, size }
                    records: data.content ?? [],
                    page: data.number ?? 0,
                    size: data.size ?? 20,
                    totalElements: data.totalElements ?? 0,
                    totalPages: data.totalPages ?? 0,
                    loading: false,
                    error: null,
                    appliedRange: { from, to },
                };
            })
            .addCase(fetchFleetServiceRecords.rejected, (state, action) => {
                state.list = {
                    ...emptyList,
                    error: action.payload || 'Failed to load your service history.',
                };
            })

            // ---- Detail ----
            .addCase(fetchFleetServiceRecordDetail.pending, (state, action) => {
                const id = action.meta.arg;
                state.detailById[id] = { data: null, loading: true, error: null };
            })
            .addCase(fetchFleetServiceRecordDetail.fulfilled, (state, action) => {
                const { id, data } = action.payload;
                state.detailById[id] = { data, loading: false, error: null };
            })
            .addCase(fetchFleetServiceRecordDetail.rejected, (state, action) => {
                const id = action.payload?.id ?? action.meta.arg;
                state.detailById[id] = {
                    data: null,
                    loading: false,
                    error: action.payload?.message || 'Failed to load this service.',
                };
            });
    },
});

export const { clearFleetServiceRecords } = fleetServiceRecordSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectFleetServiceRecordList = (state) => state.fleetServiceRecords.list;
export const selectFleetServiceRecordDetailById = (state) => state.fleetServiceRecords.detailById;

/**
 * One record's detail slot. Returns a stable default so consumers can destructure
 * without nil-guards.
 */
export const selectFleetServiceRecordDetail = (id) => (state) =>
    state.fleetServiceRecords.detailById[id]
    ?? { data: null, loading: false, error: null };

export default fleetServiceRecordSlice.reducer;